import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
} from "@mui/material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { CustomAlert } from "../../Common/Components/CustomAlert";

function AssociateShareholder({ open, handleClose, company, onAssociate }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ pourcentageAction: "0" });
  // --- ALERT STATE ---
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const generateTooltip = (row) => (
    <div style={{ lineHeight: 1.6 }}>
      <strong>Email:</strong> {row.email || "-"}
      <br />
      <strong>Cellulaire:</strong> {row.noCellulaire || "-"}
      <br />
      <strong>Langue:</strong> {row.language || "-"}
      <br /> 
      <strong>Inactif:</strong> {row.estInactif ? "Oui" : "Non"}
      <br />
      <strong>Département:</strong>{" "}
      {row.department
        ? `${row.department.textFr} / ${row.department.textEn}`
        : "-"}
      <br />
      <strong>Date Embauche:</strong>{" "}
      {row.lastLogin ? new Date(row.lastLogin).toLocaleDateString() : "-"}
      <br />
      <strong>% Action:</strong> {row.pourcentageAction || "-"}
      <br />
      <strong>ID Compagnie:</strong> {row.noCompagnie || "-"}
      <br />
      <strong>Nom Compagnie:</strong> {row.nom || "-"}
      <br />
      <strong>No Accpac:</strong> {row.noAccpac || "-"}
      <br />
      <strong>ID Département:</strong> {row.idDepartement || "-"}
      <br />
      <strong>No Adresse:</strong> {row.noAdresse || "-"}
      <br />
      <strong>Ville:</strong> {row.ville || "-"}
    </div>
  );

  const columns = [
    {
      field: "noEmploye",
      headerName: "No Employé",
      width: 120,
      hide: true, // ⬅⬅ garde la colonne invisible
    },
    {
      field: "username",
      headerName: "Username",
      width: 150,
      renderCell: (params) => (
        <Tooltip title={generateTooltip(params.row)} arrow enterTouchDelay={0}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "firstname",
      headerName: "Prénom",
      width: 150,
      renderCell: (params) => (
        <Tooltip title={generateTooltip(params.row)} arrow enterTouchDelay={0}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "lastName",
      headerName: "Nom",
      width: 150,
      renderCell: (params) => (
        <Tooltip title={generateTooltip(params.row)} arrow enterTouchDelay={0}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    if (!open) return;
    if (open) {
      // reset de l’alerte
      setAlertOpen(false);
      setAlertMessage("");
      setAlertType("success");
    }
    setLoading(true);
    setRows([]);
    setSelectedUser(null);
    setFormData({ pourcentageAction: "0" });

    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token"); 
        const res = await fetch(
      `${process.env.REACT_APP_API_URL}/Employee/GetEmployesShareHolders`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
        const data = await res.json();

        const rowsWithId = (data.employes || []).map((emp, index) => ({
          ...emp,
          id: index + 1,
        }));

        setRows(rowsWithId);
      } catch (error) {
        console.error("Erreur API :", error);
      }

      setLoading(false);
    };

    fetchUsers();
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssociate = async () => {
    if (!selectedUser) return;

    // Construire un DTO explicite
    const dto = {
      NoEmploye: selectedUser.noEmploye,
      NoCompagnie: company?.noCompagnie,
      Username: selectedUser.username,
      Firstname: selectedUser.firstname,
      LastName: selectedUser.lastName,
      Email: selectedUser.email || "",
      NoCellulaire: selectedUser.noCellulaire || "",
      Language: selectedUser.language || "",
      EstInactif: selectedUser.estInactif || false,
      IdDepartement: selectedUser.department?.id || null,
      LastLogin: selectedUser.lastLogin || null,
      NoAdresse: selectedUser.noAdresse || null,
      Ville: selectedUser.ville || "",
      PourcentageAction: Number(formData.pourcentageAction || 0),
    };

    try {
      const token = localStorage.getItem("token"); // ou d'où tu stockes ton token

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/company/AssociateShareholder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // ⬅ ici le token
          },
          body: JSON.stringify(dto),
        }
      );

      if (res.status === 409) {
        const error = await res.json();
        setAlertType("error");
        setAlertMessage(error.message);
        setAlertOpen(true);
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        setAlertType("error");
        setAlertMessage("Erreur inconnue dans AssociateShareholder");
        setAlertOpen(true);
        return;
      }

      const data = await res.json();
      console.log("Réponse backend :", data);
      // Après avoir fait le fetch POST avec succès

      if (res.ok) {
        setAlertMessage("L’employé a été associé avec succès à la compagnie !");
        setAlertType("success");
        setAlertOpen(true);
      }
      if (onAssociate) await onAssociate(); // ⬅ met à jour la grille du parent
      handleClose();
    } catch (error) {
      console.error("Erreur API :", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      sx={{ "& .MuiPaper-root": { borderRadius: 3, minHeight: "75vh" } }}
    >
      <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
        Associer un employé externe
      </DialogTitle>

      <DialogContent dividers>
        <Typography align="center" mb={2}>
          Sélectionnez un employé pour la compagnie{" "}
          <strong>{company?.name}</strong>
        </Typography>

        {/* --- Message centré dans le modal --- */}
        {alertOpen && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mb: 2,
              mt: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "14px 22px",
                borderRadius: "12px",
                minWidth: "380px",
                maxWidth: "95%",
                boxShadow: "0px 4px 15px rgba(0,0,0,0.12)",
                border: "1px solid",
                backgroundColor: alertType === "error" ? "#FFEBEE" : "#E8F5E9",
                borderColor: alertType === "error" ? "#FFCDD2" : "#C8E6C9",
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  fontSize: "22px",
                  color: alertType === "error" ? "#C62828" : "#2E7D32",
                }}
              >
                {alertType === "error" ? "❌" : "✅"}
              </Box>

              {/* Message */}
              <Typography
                sx={{
                  color: alertType === "error" ? "#C62828" : "#2E7D32",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                {alertMessage}
              </Typography>
            </Box>
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              onRowSelectionModelChange={(ids) => {
                const selectedId = ids[0];
                if (selectedUser && selectedUser.id === selectedId) {
                  setSelectedUser(null);
                  setFormData({ pourcentageAction: "0" });
                } else {
                  const user = rows.find((r) => r.id === selectedId);
                  setSelectedUser(user);
                  setFormData({
                    pourcentageAction: user.pourcentageAction || "0",
                  });
                }
              }}
              rowSelectionModel={selectedUser ? [selectedUser.id] : []}
              components={{ Toolbar: GridToolbar }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Bouton Annuler complètement à gauche */}
        <Box>
          <Button variant="outlined" onClick={handleClose}>
            Annuler
          </Button>
        </Box>

        {/* Champ % Action + Bouton Associer à droite */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {selectedUser && (
            <TextField
              label="% Action"
              name="pourcentageAction"
              type="number"
              value={formData.pourcentageAction}
              onChange={handleChange}
              sx={{ width: 150 }}
              inputProps={{ min: 0, max: 100 }}
            />
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedUser}
            onClick={handleAssociate}
          >
            Associer
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default AssociateShareholder;
